import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, shareReplay } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { JsonContentDataSource } from './json-content-data-source';
import { Banca, Agency } from '../../models/institutional/banca.model';
import { environment } from '../../../../environments/environment';

interface CmsBancasCatalogItem {
  bancaId: number;
  bancaDescription: string;
}

interface CmsBancasCatalogResponse {
  bancas: CmsBancasCatalogItem[];
}

interface CmsLocalDto {
  banca: string;
  agencia: string;
  subagencia: string;
  coordX?: number;
  coordY?: number;
  direccionLocal?: string;
  telefonoLocal?: string;
  nombreLocal?: string;
  barrio?: string;
  horariosLocal?: string;
  agenciaOficial?: boolean;
}

@Injectable()
export class CmsLocalesDataSource extends JsonContentDataSource {
  private get cmsApiUrl(): string {
    return environment.endpointBasePaths.cmsApiUrl;
  }

  constructor(http: HttpClient) {
    super(http);
  }

  private bancas$?: Observable<Banca[]>;

  override getBancas(): Observable<Banca[]> {
    if (!this.bancas$) {
      this.bancas$ = forkJoin([
        this.http.get<CmsBancasCatalogResponse>(`${this.cmsApiUrl}public/catalog/institutional/bancas`),
        super.getBancas(),
      ]).pipe(
        map(([cmsCatalog, staticBancas]) =>
          staticBancas.map((banca) => {
            const match = cmsCatalog.bancas.find((c) => c.bancaDescription.toLowerCase() === banca.name.toLowerCase());
            return match ? { ...banca, cmsBancaId: match.bancaId } : banca;
          })
        ),
        shareReplay(1)
      );
    }
    return this.bancas$;
  }

  override getAgencies(bancaId: string): Observable<Agency[]> {
    return this.getBancas().pipe(
      switchMap((bancas) => {
        const banca = bancas.find((b) => b.id === bancaId);
        if (!banca?.cmsBancaId) {
          return super.getAgencies(bancaId);
        }
        return this.http
          .get<CmsLocalDto[]>(`${this.cmsApiUrl}public/catalog/institutional/agencies/${banca.cmsBancaId}`)
          .pipe(map((locals) => locals.map((local, idx) => this.mapLocalToAgency(local, idx, bancaId, banca.name))));
      })
    );
  }

  private mapLocalToAgency(local: CmsLocalDto, idx: number, bancaId: string, bancaName: string): Agency {
    const hasCoords = (local.coordX != null && local.coordX !== 0) || (local.coordY != null && local.coordY !== 0);
    return {
      id: idx + 1,
      bancaId,
      bancaName,
      agencyNumber: local.agencia,
      subAgencyNumber: local.subagencia,
      isOfficial: local.agenciaOficial === true,
      name: local.nombreLocal ?? `Agencia ${local.agencia}/${local.subagencia}`,
      address: local.direccionLocal ?? '',
      neighborhood: local.barrio,
      phone: local.telefonoLocal,
      hours: local.horariosLocal,
      location: hasCoords ? { lat: local.coordX!, lng: local.coordY! } : undefined,
    };
  }
}
